export const CONSENT_EXAMPLE_OPTIONS = [
  { value: "none", label: "Do not include example code" },
  { value: "generic", label: "Generic" },
  { value: "borlabs", label: "Borlabs" },
  { value: "cookiebot", label: "Cookiebot" },
  { value: "complianz", label: "Complianz" },
  { value: "onetrust", label: "OneTrust" },
  { value: "usercentrics", label: "Usercentrics" },
] as const;

export type ConsentExample = (typeof CONSENT_EXAMPLE_OPTIONS)[number]["value"];

const CONSENT_EXAMPLES: Record<Exclude<ConsentExample, "none">, string> = {
  generic: `// Replace the event name and data path with values from your consent manager.
// The event must fire once with the current consent and again after every change.
window.addEventListener('your-consent-event', function(event) {
  var allowed = Boolean(event.detail && event.detail.analytics);
  CampNerd('setTrackingConsent', allowed);
});

// The underlying calls are:
// CampNerd('setTrackingConsent', true);  // granted
// CampNerd('setTrackingConsent', false); // denied or revoked`,
  borlabs: `// Adjust the service and service-group IDs for your Borlabs setup.
var campNerdBorlabsServiceId = 'campnerd-analytics';
var campNerdBorlabsServiceGroupId = 'statistics';

function updateCampNerdFromBorlabs() {
  var consents = window.BorlabsCookie && window.BorlabsCookie.Consents;
  var allowed = Boolean(
    consents &&
      consents.hasConsent(campNerdBorlabsServiceId, campNerdBorlabsServiceGroupId)
  );
  CampNerd('setTrackingConsent', allowed);
}

window.addEventListener('borlabs-cookie-after-init', updateCampNerdFromBorlabs);
window.addEventListener('borlabs-cookie-consent-saved', updateCampNerdFromBorlabs);
if (window.BorlabsCookie && window.BorlabsCookie.Consents) {
  updateCampNerdFromBorlabs();
}`,
  cookiebot: `// Adjust the consent category if CampNerd is not assigned to Statistics.
function updateCampNerdFromCookiebot() {
  var consent = window.Cookiebot && window.Cookiebot.consent;
  CampNerd('setTrackingConsent', Boolean(consent && consent.statistics));
}

window.addEventListener('CookiebotOnConsentReady', updateCampNerdFromCookiebot);
window.addEventListener('CookiebotOnAccept', updateCampNerdFromCookiebot);
window.addEventListener('CookiebotOnDecline', updateCampNerdFromCookiebot);
if (window.Cookiebot && window.Cookiebot.consent) {
  updateCampNerdFromCookiebot();
}`,
  complianz: `// Adjust the consent category if CampNerd is not assigned to Statistics.
function updateCampNerdFromComplianz() {
  var allowed =
    typeof window.cmplz_has_consent === 'function' &&
    window.cmplz_has_consent('statistics');
  CampNerd('setTrackingConsent', Boolean(allowed));
}

document.addEventListener('cmplz_cookie_warning_loaded', updateCampNerdFromComplianz);
document.addEventListener('cmplz_status_change', updateCampNerdFromComplianz);
if (typeof window.cmplz_has_consent === 'function') {
  updateCampNerdFromComplianz();
}`,
  onetrust: `// Adjust the category ID for your OneTrust tenant. C0002 is commonly Performance.
var campNerdOneTrustCategoryId = 'C0002';

function updateCampNerdFromOneTrust(event) {
  var activeGroups =
    event && Array.isArray(event.detail)
      ? event.detail
      : String(window.OnetrustActiveGroups || '').split(',');
  CampNerd('setTrackingConsent', activeGroups.includes(campNerdOneTrustCategoryId));
}

window.addEventListener('OneTrustGroupsUpdated', updateCampNerdFromOneTrust);
if (window.OnetrustActiveGroups) {
  updateCampNerdFromOneTrust();
}`,
  usercentrics: `// Adjust both values to match the custom event and service configured in Usercentrics.
var campNerdUsercentricsEventName = 'ucConsent';
var campNerdUsercentricsServiceName = 'CampNerd Analytics';

window.addEventListener(campNerdUsercentricsEventName, function(event) {
  if (!event.detail || event.detail.event !== 'consent_status') return;
  CampNerd(
    'setTrackingConsent',
    event.detail[campNerdUsercentricsServiceName] === true
  );
});`,
};

export function getConsentExample(example: ConsentExample): string {
  return example === "none" ? "" : CONSENT_EXAMPLES[example];
}
